import inspect from 'object-inspect';
import Debug from 'debug';
import { injectable, multiInject } from 'inversify';
import { Observable, Subject, Subscription } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { EventStreamListener, Event } from '@/app/event-stream';
import { TrackingPlugin, FinishError, TrackingDetails, TrackingPluginSymbol } from './tracking-api';
import { Result, err, ok } from '@xlearn/common';
import { SectionStatusChangedEventName } from '../statistics/events/section-status-events';
import { SectionOpenedEventName, SectionClosedEventName } from '../statistics/events/navigation-events';
//import { QuizCompletedEventName } from '../quiz/events/quiz-events';
//import { InteractionCompletedEventName } from '../statistics/events/interaction-events';
//import { QuestionAnsweredEventName } from '../question/events/question-events';
import { filterEventsOperator } from './event-filter';
import { Command, Query } from './common'
import { hasVueObserver, checkVueObserver } from '../shared/utils/vue-observer-utils';
//import { isInDevMode } from '../shared/utils/dev-mode';

const d = Debug('tracking-service');

export type StartErrorCode = 'not-supported-mode' | 'start-error' | 'already-started' | 'already-finished';

interface StartError {
  code: StartErrorCode;
  details?: unknown;
}

function startError(code: StartErrorCode, details?: unknown) {
  return err({ code, details });
}

type State = {
    phase: 'none'
  } | {
    phase: 'started' | 'finished'
    plugin: TrackingPlugin<Event>;
    eventSubject: Subject<Event>
    eventObservable: Observable<void>
    subscription: Subscription
  };

const statusChangeDebounceMs = 1000;

@injectable()
export class TrackingService implements EventStreamListener {

  private state: State = { phase: 'none' };

  get events(): 'all' {
    return 'all';
  }

  constructor(
    @multiInject(TrackingPluginSymbol)
    private plugins: TrackingPlugin[]) {

    
    d('creating tracking service', plugins);
  }

  public handle(event: Event): void {

    if (this.state.phase !== 'started') { return; }

    // TODO: remove this
    const handledEvents = [ SectionStatusChangedEventName,
                            SectionOpenedEventName, SectionClosedEventName,
                            //QuizCompletedEventName,
                            //InteractionCompletedEventName,
                            //QuestionAnsweredEventName
                          ] as string[];

    if (handledEvents.indexOf(event.name) >= 0) {

      const immutable = JSON.stringify(event);
      // console.table(immutable);
      this.state.eventSubject.next(event);

      // TODO: remove
      setTimeout(() => {
        if (immutable !== JSON.stringify(event)) {
          throw new Error(`Event changed after it fired ${immutable} -> ${JSON.stringify(event)}`);
        }
      }, 1000);
    }
  }

  public async start(details: TrackingDetails): Promise<Result<true, StartError>> {

    d('tracking start', details)
    if (this.state.phase === 'started') {
      return startError('already-started');
    }

    if (this.state.phase === 'finished') {
      return startError('already-finished');
    }

    this.plugins.sort((x, y) => x.details.priority - y.details.priority);
    const plugins = [ ...this.plugins ];
    let foundPlugin: TrackingPlugin | null = null;
    for (const plugin of plugins) {
      if (await plugin.details.isAvailable(details)) {
        foundPlugin = plugin;
        break;
      }
    }

    if (!foundPlugin) {
      return startError('not-supported-mode');
    }

    console.info(`Using tracking plugin: ${foundPlugin.details.name}`);

    const pluginResult = await foundPlugin.start(details);
    if (!pluginResult.success) {
      return startError('start-error', pluginResult.error);
    }

    const eventSubject = new Subject<Event>();
    const eventObservable = eventSubject.pipe(
      filterEventsOperator({ statusChangeDebounceMs }),
      concatMap(async (x) => {
        // console.log('EVENT START')
        // console.table(JSON.parse(JSON.stringify(x)));
        // console.log('EVENT END')
        if (foundPlugin) {
          await foundPlugin.handleEvent(x);
        }
      } )
    );

    const subscription = eventObservable.subscribe();

    this.state = {
      phase: 'started',
      plugin: foundPlugin,
      subscription,
      eventSubject,
      eventObservable
    };

      // const self = this
      // function openSection(id: string, title: string = id) {
      //   self.plugin!.handleEvent(createEvent('section-opened', {
      //     sectionId: id,
      //     title
      //   }));
      // }

      // function closeSection(id: string, title: string, details: { durationSeconds: number }) {
      //   self.plugin!.handleEvent(createEvent('section-closed', {
      //     sectionId: id,
      //     title,
      //     durationSeconds: details.durationSeconds
      //   }));
      // }

      // console.log('SECTION STATE', await this.querySectionState(['section1']))

      // openSection('section1', 'Section 1')
      // closeSection('section1', 'Section 1', { durationSeconds: 42 })

    return ok(true);
  }

  public execute(command: Command) {

    if (this.state.phase !== 'started') {
      throw new Error('Not started')
    }
    
    d('execute', command);

    command = checkVueObserver(command)    
    this.state.plugin.execute(command)
  }

  public async query<T extends string, TPayload, TResult>(query: Query<T, TPayload, TResult>): Promise<TResult> {
    d('query', query);

    if (this.state.phase !== 'started') {
      throw new Error('Not started')
    }

    const result = await this.state.plugin.query(query)
    d('query result', result)

    return result
  }

  public async finish(): Promise<Result<true, FinishError>> {
    d('tracking finish');

    if (this.state.phase !== 'started') {
      return err('not-started');
    }

    this.state.phase = 'finished';
    this.state.eventSubject.complete();
    this.state.subscription.unsubscribe();
    await this.state.plugin.finish();

    return ok(true);
  }
}
